import { useTranslation } from "react-i18next";

import { View } from "services/databases/getViews";
import { useViewNames } from "services/databases/useViewNames";

import ContextMenuDivider from "components/ContextMenu/ContextMenuDivider";
import ContextMenuItem from "components/ContextMenu/ContextMenuItem";
import { WithSearchVirtualized } from "components/OutlinedSelect/OutlinedSelect";

import { ROLES_WIZARD_SET_RESOURCE_OBJECTS } from "../privilegesReducer";
import { usePrivilegesState } from "../usePrivilegesContext";
import { SelectItems } from "./SelectItems";
import { ALL_VIEWS } from "./constant";

type Props = {
  catalogName: string;
  index: number;
  resource: string;
};

export const ViewsItems = (props: Props) => {
  const { index, catalogName, resource } = props;
  const { privilegesState, privilegesDispatch } = usePrivilegesState();
  const views = useViewNames({ database: catalogName });
  const { t } = useTranslation();

  const groupState = privilegesState.view[index];
  const { objects } = groupState;

  const selectedViews = objects
    .filter(object => object.catalogName === catalogName)
    .map(object => object.name);

  const filterBulkResources = (item: { name: string; catalogName: string }) => {
    if (item.catalogName !== catalogName) {
      return true;
    }
    if (item.catalogName === catalogName) {
      return item.name !== ALL_VIEWS;
    }
  };

  const filterDatabaseResources = (item: {
    name: string;
    catalogName: string;
  }) => {
    if (item.catalogName !== catalogName) {
      return true;
    }
    return false;
  };

  const handleBulkResource = (name: string) => {
    const selectedObjects = objects.filter(filterDatabaseResources);

    privilegesDispatch({
      type: ROLES_WIZARD_SET_RESOURCE_OBJECTS,
      resource,
      objects: selectedViews.includes(name)
        ? [...selectedObjects]
        : [...selectedObjects, { catalogName, name }],
      index,
    });
  };

  const handleResource = (view: { tableName: string }) => {
    const selectedObjects = selectedViews.includes(view.tableName)
      ? objects.filter(object => {
          if (object.catalogName !== catalogName) {
            return true;
          }
          return object.name !== view.tableName;
        })
      : [...objects, { name: view.tableName, catalogName }].filter(
          filterBulkResources
        );

    privilegesDispatch({
      type: ROLES_WIZARD_SET_RESOURCE_OBJECTS,
      resource,
      index,
      database: catalogName,
      objects: selectedObjects,
    });
  };

  const prefixItems = [
    <ContextMenuItem
      value={ALL_VIEWS}
      key={ALL_VIEWS}
      checked={selectedViews.includes(ALL_VIEWS)}
      checkedIconPlaceholder={true}
      text={t("roles_wizard.privileges.all_views")}
      direction="left"
      bulkItem
      skipFilter
      onClick={() => {
        handleBulkResource(ALL_VIEWS);
      }}
    />,
    <ContextMenuDivider key="divider" />,
  ];

  const renderItem = (view: View) => {
    return (
      <ContextMenuItem
        key={view.tableName}
        value={view.tableName}
        text={view.tableName}
        direction="left"
        checked={selectedViews.includes(view.tableName)}
        onClick={() => {
          handleResource(view);
        }}
      />
    );
  };

  return (
    <WithSearchVirtualized
      renderItem={renderItem}
      prefixItems={prefixItems}
      items={views}
      searchOptions={{
        searchPlaceholder: t("roles_wizard.privileges.search_view"),
        searchKey: "tableName",
      }}
    >
      <SelectItems />
    </WithSearchVirtualized>
  );
};
